import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { Dialog } from '../../common/Dialog/Dialog'
import { TransparentButton, ButtonType } from '../../common/Buttons/TransparentButton'
import './StatusModal.scss'
import {
  isSupplierOrder,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../../redux/api/ordersApi'
import { useAuth } from '../../../auth/UseAuth'
import { OrderInternalViewModel, OrderSuppliersViewModel, WorkflowOrderStepStatus } from '@ail/orders-api'

interface StatusModalProps {
  order?: OrderInternalViewModel | OrderSuppliersViewModel
  onClose: () => void
}

export function StatusModal(props: StatusModalProps) {
  const [status, setStatus] = useState(WorkflowOrderStepStatus.New)
  const { t } = useTranslation()
  const { user } = useAuth()

  const [updateOrderInternal] = useUpdateOrderInternalMutation({ fixedCacheKey: 'UpdateInternal' })
  const [updateOrderSuppliers] = useUpdateOrderSuppliersMutation({ fixedCacheKey: 'UpdateSuppliers' })

  const enabledStatuses = useMemo(() => {
    return Object.values(WorkflowOrderStepStatus).reduce(
      (result, status) => {
        switch (status) {
          case WorkflowOrderStepStatus.New:
            result[status] = !!user?.isAdmin
            break
          case WorkflowOrderStepStatus.InProgress:
            result[status] = props.order?.form.status !== WorkflowOrderStepStatus.Processed
            break
          case WorkflowOrderStepStatus.Processed:
            result[status] = !!user?.isAdmin
            break
          case WorkflowOrderStepStatus.Done:
            result[status] = true
            break
          case WorkflowOrderStepStatus.Pending:
            result[status] = user?.isAdmin || props.order?.form.status !== WorkflowOrderStepStatus.Done
            break
          case WorkflowOrderStepStatus.OnHold:
            result[status] = true
            break
          case WorkflowOrderStepStatus.Cancelled:
            result[status] = user?.isInternalUser || props.order?.form.status !== WorkflowOrderStepStatus.Done
            break
          case WorkflowOrderStepStatus.Recreated:
            result[status] = false
            break
          case WorkflowOrderStepStatus.Archived:
            result[status] = !!user?.isInternalUser
            break
        }
        return result
      },
      {} as Record<WorkflowOrderStepStatus, boolean>
    )
  }, [props.order?.form.status, user?.isAdmin, user?.isInternalUser])

  const hasEnabledStatus = useMemo(() => {
    return Object.values(enabledStatuses).some((enabled) => enabled)
  }, [enabledStatuses])

  useEffect(() => {
    if (props.order) {
      setStatus(props.order.form.status)
    }
  }, [props.order])

  const update = () => {
    if (!props.order) {
      return
    }
    const { order } = props

    const payload = {
      id: order.id,
      model: {
        form: { status, fields: [] },
      },
    }

    if (isSupplierOrder(order)) {
      updateOrderSuppliers(payload)
    } else {
      updateOrderInternal(payload)
    }

    props.onClose()
  }

  return (
    <Dialog
      open={!!props.order && hasEnabledStatus}
      title={t('STATUS')}
      onClose={props.onClose}
      icon="icon-marker"
      width={450}
    >
      <div className="status-modal">
        <div className="selectArea">
          <select value={status} onChange={(e) => setStatus(e.target.value as WorkflowOrderStepStatus)}>
            <option value="New" disabled={!enabledStatuses[WorkflowOrderStepStatus.New]}>
              {t('NEW')}
            </option>
            <option value="Pending" disabled={!enabledStatuses[WorkflowOrderStepStatus.Pending]}>
              {t('PENDING')}
            </option>
            <option value="OnHold" disabled={!enabledStatuses[WorkflowOrderStepStatus.OnHold]}>
              {t('ONHOLD')}
            </option>
            <option value="InProgress" disabled={!enabledStatuses[WorkflowOrderStepStatus.InProgress]}>
              {t('INPROGRESS')}
            </option>
            <option value="Done" disabled={!enabledStatuses[WorkflowOrderStepStatus.Done]}>
              {t('DONE')}
            </option>
            <option value="Archived" disabled={!enabledStatuses[WorkflowOrderStepStatus.Archived]}>
              {t('ARCHIVED')}
            </option>
            <option value="Cancelled" disabled={!enabledStatuses[WorkflowOrderStepStatus.Cancelled]}>
              {t('CANCELLED')}
            </option>
          </select>
          <i className="icon-bottom" />
        </div>
        <div className="status-modal-actions">
          <TransparentButton type={ButtonType.Negative} icon="icon-close" onClick={props.onClose} name={t('CANCEL')} />
          <TransparentButton type={ButtonType.Positive} icon="icon-check" onClick={update} name={t('CONFIRM')} />
        </div>
      </div>
    </Dialog>
  )
}
